import { type Routes } from '@medely/ui-kit';
import { MultiFactorError } from 'firebase/auth';
import {
  ArrayParam,
  BooleanParam,
  JsonParam,
  NumberParam,
  StringParam,
  withDefault,
} from 'use-query-params';

export type ForwardRoute = {
  [K in keyof ProRoutes]: {
    forwardTo: K;
    forwardParams: ProRoutes[K];
  };
}[keyof ProRoutes];

// Key = Route name
// Value = Params type
// Optional params should use | undefined, not ?
// Currently only primitive values are supported.
export type ProRoutes = {
  AdjustmentDetail: {
    id: number;
  };
  Application: never;
  AvailableAssignmentsLocal: never;
  AvailableJobDetails: {
    id: number;
    book?: boolean;
  };
  EarningsPayoutDetails: {
    id: number;
  };
  EarningsPending: never;
  JobDetails: {
    id?: number | null;
  };
  Shifts: {
    backRoute?: string;
  };
  Profile: never;
  ProPoolInvite: {
    locationId: number;
    professionalQualificationId: number;
  };
  ProPoolInviteAccepted: {
    facilityName: string;
  };
  LocationOnboarding: {
    locationId?: number;
    positionIds?: string;
    poolId?: number;
  };
  Today: never;
  PositionsAndSpecialties: never;
  PayoutTaxes: never;
  Credentials: never;
  RootCredentials: never;
  AccountDetails: { id: number | string };
  BackgroundCheckDisclosure: never;
  PayoutMethod: Partial<{ id: number | string }>;
  TaxInformation: Partial<{ id: number | string }>;
  MissingAndExpiringConditions: never;
  FindJobs: never;
  FindJobsPermanentPlacements: never;
  MfaSmsCodeEntryScreen:
    | { firebaseError?: MultiFactorError }
    | {
        forward?: ForwardRoute;
        flow?: string;
      };
  PermanentPlacementAvailableDetails: { id: number };
  SignIn: never;
  MfaIdentityVerificationScreen: {
    forward?: ForwardRoute;
    flow?: string;
  };
  Personal: never;
  CredentialDetails: {
    credential: never;
  };
  PasswordReset: never;
  IdentityVerification: never;
  GoToAssignmentChecklist: {
    assignmentId: number;
  };
  OrientationJobsList: {
    poolId: number;
  };
  PoolInterestSubmitted: {
    poolId: number;
  };
  PermanentPlacementAvailableDetailsInfo: { id?: number | null };
  ShiftSwaps: never;
  ReferenceForm: never;
  ReferenceEditForm: { id: number };
  References: never;
  ResumeBuilder: never;
  ViewReference: { id: number };
  EditClockTimes: { jobId: string };
  SchedulingPreferences: never;
};

export const proRoutes: Routes<ProRoutes> = {
  AdjustmentDetail: {
    url: '/earnings/adjustments/:id',
  },
  Application: {
    url: '/application',
  },
  AvailableAssignmentsLocal: {
    url: '/available/assignments/local',
  },
  AvailableJobDetails: {
    url: '/available/jobs/:id',
  },
  PermanentPlacementAvailableDetails: {
    url: '/available/permanent-placements/:id',
  },
  EarningsPayoutDetails: {
    url: '/earnings/payouts/:id',
  },
  EarningsPending: {
    url: '/earnings/pending',
  },
  JobDetails: {
    url: '/jobs/:id',
  },
  Shifts: {
    url: '/schedule',
  },
  Profile: {
    url: '/profile',
  },
  ProPoolInvite: {
    url: '/pro-pool/invite',
  },
  ProPoolInviteAccepted: {
    url: '/pro-pool/invite-accepted',
  },
  LocationOnboarding: {
    url: 'onboarding/location',
  },
  Today: {
    url: '/',
  },
  PositionsAndSpecialties: {
    url: '/profile/positions-and-specialties',
  },
  PayoutTaxes: {
    url: '/settings/financial',
  },
  AccountDetails: {
    url: '/settings/financial/:id/details',
  },
  BackgroundCheckDisclosure: {
    url: '/onboarding/background_check/disclosure',
  },
  PayoutMethod: {
    url: '/settings/financial/payout-method',
  },
  TaxInformation: {
    url: '/settings/financial/taxinfo',
  },
  Credentials: {
    url: '/credentials',
  },
  RootCredentials: {
    url: '/RootCredentials',
  },
  MissingAndExpiringConditions: {
    url: '/credentials/status',
  },
  FindJobsPermanentPlacements: {
    url: '/available/permanent-placements',
  },
  FindJobs: {
    url: '/available/jobs',
  },
  SignIn: {
    url: '/login',
  },
  MfaSmsCodeEntryScreen: {
    url: '/mfa-entry',
  },
  MfaIdentityVerificationScreen: {
    url: '/mfa-verification',
  },
  Personal: {
    url: '/profile/personal',
  },
  CredentialDetails: {
    url: '/credentials',
  },
  PasswordReset: {
    url: '/settings/password',
  },
  IdentityVerification: {
    url: '/settings/identity',
  },
  GoToAssignmentChecklist: {
    url: '/available/assignments/:assignmentId/eligibility/license',
  },
  OrientationJobsList: {
    url: '/pro-pool/orientation/jobs',
  },
  PoolInterestSubmitted: {
    url: '/pro-pool/interest-submitted',
  },
  PermanentPlacementAvailableDetailsInfo: {
    url: '/available/permanent-placements/:id/details',
  },
  ShiftSwaps: {
    url: '/shift-swaps',
  },
  ReferenceForm: {
    url: '/profile/references/create',
  },
  ReferenceEditForm: {
    url: '/profile/references/:id/edit',
  },
  References: {
    url: '/profile/references',
  },
  ResumeBuilder: {
    url: '/profile/resume',
  },
  ViewReference: {
    url: '/profile/references/:id',
  },
  EditClockTimes: {
    url: '/jobs/:jobId/edit-clock-times',
  },
  SchedulingPreferences: {
    url: '/scheduling-preferences',
  },
};

export type RouteParamsWeb<T> = {
  [K in keyof T]:
    | Record<string, never>
    | {
        [key: string]:
          | typeof NumberParam
          | typeof StringParam
          | typeof BooleanParam
          | typeof ArrayParam
          | typeof JsonParam;
      };
};

export const proQueryParams: RouteParamsWeb<ProRoutes> = {
  AdjustmentDetail: {
    id: NumberParam,
  },
  Application: {},
  AvailableAssignmentsLocal: {},
  AvailableJobDetails: {
    id: NumberParam,
  },
  EarningsPayoutDetails: {
    id: NumberParam,
  },
  EarningsPending: {},
  JobDetails: {
    id: NumberParam,
  },
  Profile: {},
  ProPoolInvite: {
    locationId: NumberParam,
    professionalQualificationId: NumberParam,
  },
  ProPoolInviteAccepted: {
    facilityName: StringParam,
  },
  LocationOnboarding: {
    locationId: NumberParam,
    positionIds: StringParam,
    poolId: NumberParam,
  },
  Today: {},
  PayoutTaxes: {},
  TaxInformation: {},
  AccountDetails: {},
  BackgroundCheckDisclosure: {},
  Credentials: {},
  MissingAndExpiringConditions: {},
  PayoutMethod: {},
  PositionsAndSpecialties: {},
  RootCredentials: {},
  Shifts: {},
  FindJobs: {},
  FindJobsPermanentPlacements: {},
  PermanentPlacementAvailableDetails: {
    id: NumberParam,
  },
  SignIn: {},
  MfaSmsCodeEntryScreen: {
    forward: JsonParam,
    firebaseError: withDefault(JsonParam, null),
    flow: withDefault(StringParam, 'SENSITIVE_SCREEN'),
  },
  MfaIdentityVerificationScreen: {
    forward: JsonParam,
    flow: withDefault(StringParam, 'SENSITIVE_SCREEN'),
  },
  Personal: {},
  CredentialDetails: {
    credential: JsonParam,
  },
  PasswordReset: {},
  IdentityVerification: {},
  GoToAssignmentChecklist: {
    assignmentId: NumberParam,
  },
  OrientationJobsList: {
    poolId: NumberParam,
  },
  PoolInterestSubmitted: {
    poolId: NumberParam,
  },
  PermanentPlacementAvailableDetailsInfo: {
    id: NumberParam,
  },
  ShiftSwaps: {},
  ReferenceForm: {},
  ReferenceEditForm: {
    id: NumberParam,
  },
  References: {},
  ResumeBuilder: {},
  ViewReference: {
    id: NumberParam,
  },
  EditClockTimes: {
    jobId: StringParam,
  },
  SchedulingPreferences: {},
};
